import { Inject, Injectable } from '@angular/core';
import {
    HubConnection,
    HubConnectionBuilder,
    HubConnectionState,
} from '@microsoft/signalr';
import { from, Observable, of } from 'rxjs';
import {
    catchError,
    delay,
    mapTo,
    retryWhen,
    scan,
    switchMap,
    timeout,
} from 'rxjs/operators';

import { AppConfig, CMPS_APP_CONFIG } from '../app.config';

import { checkBuildNumber } from './check-build-number';

enum HubMethods {
    CheckBuildNumber = 'checkBuildNumber',
}

@Injectable()
export class SystemNotifierService {
    private _stopped: boolean;
    private _conn: HubConnection;

    constructor(
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
    ) {
        this._build();
    }

    start(): Observable<boolean> {
        this._stopped = false;
        if (
            !this._conn ||
            this._conn.state !== HubConnectionState.Disconnected
        ) {
            return of(false);
        }
        return of(null).pipe(
            switchMap(() => from(this._conn.start())),
            retryWhen((errors) =>
                errors.pipe(
                    delay(750),
                    scan((acc) => {
                        if (acc >= 2) {
                            throw new Error('retry failed');
                        }
                        return acc + 1;
                    }, 0),
                ),
            ),
            timeout(2000),
            mapTo(true),
            catchError((err) => {
                console.log(err);
                return of(false);
            }),
        );
    }

    stop(): Observable<boolean> {
        this._stopped = true;
        if (!this._conn) {
            return of(false);
        }
        return of(null).pipe(
            switchMap(() => from(this._conn.stop())),
            mapTo(true),
            catchError((err) => {
                console.log(err);
                return of(false);
            }),
        );
    }

    private _build(): void {
        if (
            !this._appConfig.SERVICE_ENDPOINTS ||
            !this._appConfig.SERVICE_ENDPOINTS.SYSTEM_NOTIFIER
        ) {
            return;
        }

        this._conn = new HubConnectionBuilder()
            .withUrl(
                `${this._appConfig.SERVICE_ENDPOINTS.SYSTEM_NOTIFIER}/uiConfigurationHub`,
            )
            .build();

        this._conn.on(HubMethods.CheckBuildNumber, checkBuildNumber);

        this._conn.onclose(() => {
            if (!this._stopped) {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                this.start().toPromise();
            }
        });
    }
}
